import { useQuery, useMutation } from '@tanstack/react-query';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import type {
  Plantilla,
  EventTheme,
  WizardFormData,
  ProviderSummary,
  InvitationWizardFormData,
  RenderWizardFormData,
} from '@/types/wizard';
import type { PresentationItem } from '@/types/presentation';

export const usePlantillas = () =>
  useQuery<Plantilla[]>({
    queryKey: ['plantillas'],
    queryFn: () => api.get(API_ROUTES.plantillas).then((r) => r.data),
  });

export const useThemes = () =>
  useQuery<EventTheme[]>({
    queryKey: ['themes'],
    queryFn: () => api.get(API_ROUTES.themes).then((r) => r.data),
  });

interface WizardSearchFilters {
  category?: string;
  service?: string;
  min_rating?: number;
  city?: string;
  currency?: string;
}

interface WizardSearchResult {
  results: ProviderSummary[];
  similar: ProviderSummary[];
}

export const useWizardProviderSearch = (search: string, filters?: WizardSearchFilters) =>
  useQuery<WizardSearchResult>({
    queryKey: ['wizard-providers', search, filters],
    queryFn: () =>
      api
        .get('/api/providers/providers/wizard-search/', {
          params: { search, ...filters },
        })
        .then((r) => r.data),
    staleTime: 30_000,
  });

export const useServicesLight = () =>
  useQuery<{ id: string; name: string; slug: string }[]>({
    queryKey: ['services-light'],
    queryFn: () => api.get(API_ROUTES.servicesLight).then((r) => r.data),
    staleTime: 5 * 60_000,
  });

export const useProviderCities = () =>
  useQuery<string[]>({
    queryKey: ['provider-cities'],
    queryFn: () => api.get('/api/providers/providers/cities/').then((r) => r.data),
    staleTime: 5 * 60_000,
  });

export const useWizardGenerate = () =>
  useMutation<PresentationItem, Error, WizardFormData>({
    mutationFn: (data) => api.post(API_ROUTES.wizardGenerate, data).then((r) => r.data),
  });

export const useWizardGenerateInvitation = () =>
  useMutation<PresentationItem, Error, InvitationWizardFormData>({
    mutationFn: (data) =>
      api.post(API_ROUTES.wizardGenerate, { ...data, type: 'invitation' }).then((r) => r.data),
  });

export const useWizardGenerateRenders = () =>
  useMutation<PresentationItem, Error, RenderWizardFormData>({
    mutationFn: (data) =>
      api.post(API_ROUTES.wizardGenerate, { ...data, type: 'render' }).then((r) => r.data),
  });

export const useDriveUpload = () =>
  useMutation<
    { drive_file_id: string; drive_url: string },
    Error,
    { presentationId: string; access_token?: string }
  >({
    mutationFn: ({ presentationId, access_token }) =>
      api.post(`${API_ROUTES.driveUpload}${presentationId}/`, { access_token }).then((r) => r.data),
  });

export const useDrivePull = () =>
  useMutation<{ pptx_url: string }, Error, { presentationId: string; access_token?: string }>({
    mutationFn: ({ presentationId, access_token }) =>
      api.post(`${API_ROUTES.drivePull}${presentationId}/`, { access_token }).then((r) => r.data),
  });

export const useOnlyOfficeForcesave = () =>
  useMutation<
    { saved: boolean; onlyoffice_error?: number; detail?: string },
    Error,
    string
  >({
    mutationFn: (presentationId) =>
      api
        .post(`/api/providers/editor/onlyoffice/forcesave/${presentationId}/`)
        .then((r) => r.data),
  });

export const useRebuildPptx = () =>
  useMutation<PresentationItem, Error, string>({
    mutationFn: (presentationId) =>
      api.post(`${API_ROUTES.presentations}${presentationId}/rebuild-pptx/`).then((r) => r.data),
  });

export const useOnlyOfficeConfig = (
  presentationId: string | null,
  mode: 'edit' | 'view' = 'edit',
) =>
  useQuery<{
    config: Record<string, unknown>;
    onlyoffice_url: string;
  }>({
    queryKey: ['onlyoffice-config', presentationId, mode],
    queryFn: () =>
      api
        .get(`${API_ROUTES.editorOnlyofficeConfig}${presentationId}/`, { params: { mode } })
        .then((r) => r.data),
    enabled: !!presentationId,
    staleTime: 0, // Always refetch (doc key changes)
  });
